/**
 * Decoding catalog for the `machine_update` signals (schema version 1).
 *
 * The agroNave sends the raw J1939 field of each signal; the decoded value is
 * `raw * resolution + offset`, in the unit below. Groups and their order follow
 * the agroNave "Machine Diagnostics" screen, as documented in the protocol wiki.
 */

export const MACHINE_I18N_PREFIX = 'monitoring.tabs.machine';

const SIGNAL_LABEL_PREFIX = `${MACHINE_I18N_PREFIX}.signals`;
const SIGNAL_SHORT_LABEL_PREFIX = `${MACHINE_I18N_PREFIX}.signals-short`;
const KPI_LABEL_PREFIX = `${MACHINE_I18N_PREFIX}.kpis`;
const SIGNAL_VALUE_PREFIX = `${MACHINE_I18N_PREFIX}.signal-values`;
const GROUP_PREFIX = `${MACHINE_I18N_PREFIX}.groups`;

export enum MachineSignalKind {
  Measurement = 'measurement',
  State = 'state',
}

export enum MachineSignalSpn {
  EngineSpeed = 190,
  EngineLoad = 92,
  EngineHours = 247,
  CoolantTemperature = 110,
  OilTemperature = 175,
  IntakeAirTemperature = 105,
  AmbientTemperature = 171,
  OilPressure = 100,
  BoostPressure = 102,
  OilLevel = 98,
  CoolantLevel = 111,
  FuelLevel = 96,
  FuelRate = 183,
  WaterInFuel = 97,
  BatteryVoltage = 168,
  AcceleratorPedal = 91,
  RemoteAcceleratorPedal = 974,
  ServiceBrake = 597,
  Clutch = 598,
  ParkingBrake = 619,
  WheelSpeed = 84,
}

export interface MachineSignalDefinition {
  spn: MachineSignalSpn;
  /** Full name, used where the signal stands alone (fault codes). */
  labelKey: string;
  /** Name inside its group card, where the group title gives the context. */
  shortLabelKey: string;
  /** Name on a summary tile. */
  kpiLabelKey: string;
  kind: MachineSignalKind;
  resolution: number;
  offset: number;
  unit: string;
  decimals: number;
  /** For state signals: i18n keys of the meaning of raw 0 and raw 1, in this order. */
  stateValueKeys?: [string, string];
}

export enum MachineSignalGroupId {
  Engine = 'engine',
  Temperatures = 'temperatures',
  Pressures = 'pressures',
  Levels = 'levels',
  Fuel = 'fuel',
  Electrical = 'electrical',
  OperatorControls = 'operator-controls',
}

export interface MachineSignalGroupDefinition {
  id: MachineSignalGroupId;
  titleKey: string;
  signals: MachineSignalDefinition[];
}

const UNIT_RPM = 'rpm';
const UNIT_PERCENT = '%';
const UNIT_HOURS = 'h';
const UNIT_CELSIUS = '°C';
const UNIT_KILOPASCAL = 'kPa';
const UNIT_LITERS_PER_HOUR = 'L/h';
const UNIT_VOLT = 'V';
const UNIT_KM_PER_HOUR = 'km/h';

const NO_OFFSET = 0;
const CELSIUS_8_BIT_OFFSET = -40;
const CELSIUS_16_BIT_OFFSET = -273;

const NO_YES_KEYS: [string, string] = [
  `${SIGNAL_VALUE_PREFIX}.no`,
  `${SIGNAL_VALUE_PREFIX}.yes`,
];
const RELEASED_PRESSED_KEYS: [string, string] = [
  `${SIGNAL_VALUE_PREFIX}.released`,
  `${SIGNAL_VALUE_PREFIX}.pressed`,
];
const RELEASED_APPLIED_KEYS: [string, string] = [
  `${SIGNAL_VALUE_PREFIX}.released`,
  `${SIGNAL_VALUE_PREFIX}.applied`,
];

function labelKeys(
  label: string,
  kpiLabelKey?: string,
): Pick<MachineSignalDefinition, 'labelKey' | 'shortLabelKey' | 'kpiLabelKey'> {
  const labelKey = `${SIGNAL_LABEL_PREFIX}.${label}`;
  return {
    labelKey,
    shortLabelKey: `${SIGNAL_SHORT_LABEL_PREFIX}.${label}`,
    kpiLabelKey: kpiLabelKey ?? labelKey,
  };
}

function measurement(
  spn: MachineSignalSpn,
  label: string,
  resolution: number,
  offset: number,
  unit: string,
  decimals: number,
  kpiLabelKey?: string,
): MachineSignalDefinition {
  return {
    spn,
    ...labelKeys(label, kpiLabelKey),
    kind: MachineSignalKind.Measurement,
    resolution,
    offset,
    unit,
    decimals,
  };
}

function state(
  spn: MachineSignalSpn,
  label: string,
  stateValueKeys: [string, string],
): MachineSignalDefinition {
  return {
    spn,
    ...labelKeys(label),
    kind: MachineSignalKind.State,
    resolution: 1,
    offset: NO_OFFSET,
    unit: '',
    decimals: 0,
    stateValueKeys,
  };
}

export const MACHINE_SIGNAL_GROUPS: MachineSignalGroupDefinition[] = [
  {
    id: MachineSignalGroupId.Engine,
    titleKey: `${GROUP_PREFIX}.engine`,
    signals: [
      measurement(
        MachineSignalSpn.EngineSpeed,
        'engine-speed',
        0.125,
        NO_OFFSET,
        UNIT_RPM,
        0,
      ),
      measurement(
        MachineSignalSpn.EngineLoad,
        'engine-load',
        1,
        NO_OFFSET,
        UNIT_PERCENT,
        0,
      ),
      measurement(
        MachineSignalSpn.EngineHours,
        'engine-hours',
        0.05,
        NO_OFFSET,
        UNIT_HOURS,
        1,
      ),
    ],
  },
  {
    id: MachineSignalGroupId.Temperatures,
    titleKey: `${GROUP_PREFIX}.temperatures`,
    signals: [
      measurement(
        MachineSignalSpn.CoolantTemperature,
        'coolant-temperature',
        1,
        CELSIUS_8_BIT_OFFSET,
        UNIT_CELSIUS,
        0,
        `${KPI_LABEL_PREFIX}.coolant-temperature`,
      ),
      measurement(
        MachineSignalSpn.OilTemperature,
        'oil-temperature',
        0.03125,
        CELSIUS_16_BIT_OFFSET,
        UNIT_CELSIUS,
        1,
      ),
      measurement(
        MachineSignalSpn.IntakeAirTemperature,
        'intake-air-temperature',
        1,
        CELSIUS_8_BIT_OFFSET,
        UNIT_CELSIUS,
        0,
      ),
      measurement(
        MachineSignalSpn.AmbientTemperature,
        'ambient-temperature',
        0.03125,
        CELSIUS_16_BIT_OFFSET,
        UNIT_CELSIUS,
        1,
      ),
    ],
  },
  {
    id: MachineSignalGroupId.Pressures,
    titleKey: `${GROUP_PREFIX}.pressures`,
    signals: [
      measurement(
        MachineSignalSpn.OilPressure,
        'oil-pressure',
        4,
        NO_OFFSET,
        UNIT_KILOPASCAL,
        0,
      ),
      measurement(
        MachineSignalSpn.BoostPressure,
        'boost-pressure',
        2,
        NO_OFFSET,
        UNIT_KILOPASCAL,
        0,
      ),
    ],
  },
  {
    id: MachineSignalGroupId.Levels,
    titleKey: `${GROUP_PREFIX}.levels`,
    signals: [
      measurement(
        MachineSignalSpn.OilLevel,
        'oil-level',
        0.4,
        NO_OFFSET,
        UNIT_PERCENT,
        0,
      ),
      measurement(
        MachineSignalSpn.CoolantLevel,
        'coolant-level',
        0.4,
        NO_OFFSET,
        UNIT_PERCENT,
        0,
      ),
    ],
  },
  {
    id: MachineSignalGroupId.Fuel,
    titleKey: `${GROUP_PREFIX}.fuel`,
    signals: [
      measurement(
        MachineSignalSpn.FuelLevel,
        'fuel-level',
        0.4,
        NO_OFFSET,
        UNIT_PERCENT,
        0,
      ),
      measurement(
        MachineSignalSpn.FuelRate,
        'fuel-rate',
        0.05,
        NO_OFFSET,
        UNIT_LITERS_PER_HOUR,
        1,
      ),
      state(MachineSignalSpn.WaterInFuel, 'water-in-fuel', NO_YES_KEYS),
    ],
  },
  {
    id: MachineSignalGroupId.Electrical,
    titleKey: `${GROUP_PREFIX}.electrical`,
    signals: [
      measurement(
        MachineSignalSpn.BatteryVoltage,
        'battery-voltage',
        0.05,
        NO_OFFSET,
        UNIT_VOLT,
        1,
      ),
    ],
  },
  {
    id: MachineSignalGroupId.OperatorControls,
    titleKey: `${GROUP_PREFIX}.operator-controls`,
    signals: [
      measurement(
        MachineSignalSpn.AcceleratorPedal,
        'accelerator-pedal',
        0.4,
        NO_OFFSET,
        UNIT_PERCENT,
        0,
      ),
      measurement(
        MachineSignalSpn.RemoteAcceleratorPedal,
        'remote-accelerator-pedal',
        0.4,
        NO_OFFSET,
        UNIT_PERCENT,
        0,
      ),
      state(
        MachineSignalSpn.ServiceBrake,
        'service-brake',
        RELEASED_PRESSED_KEYS,
      ),
      state(MachineSignalSpn.Clutch, 'clutch', RELEASED_PRESSED_KEYS),
      state(
        MachineSignalSpn.ParkingBrake,
        'parking-brake',
        RELEASED_APPLIED_KEYS,
      ),
      measurement(
        MachineSignalSpn.WheelSpeed,
        'wheel-speed',
        0.00390625,
        NO_OFFSET,
        UNIT_KM_PER_HOUR,
        1,
      ),
    ],
  },
];

/**
 * How the groups stack in the "every signal" accordion: one column per entry,
 * top to bottom. Each column adds up to five or six signals, so the columns end
 * at about the same height.
 */
export const MACHINE_SIGNAL_COLUMNS: MachineSignalGroupId[][] = [
  [MachineSignalGroupId.Engine, MachineSignalGroupId.Pressures],
  [MachineSignalGroupId.Fuel, MachineSignalGroupId.Levels],
  [MachineSignalGroupId.Temperatures, MachineSignalGroupId.Electrical],
  [MachineSignalGroupId.OperatorControls],
];

/** Readings shown as summary tiles above the accordions, in this order. */
export const MACHINE_KPI_SPNS: MachineSignalSpn[] = [
  MachineSignalSpn.EngineSpeed,
  MachineSignalSpn.EngineLoad,
  MachineSignalSpn.CoolantTemperature,
  MachineSignalSpn.OilPressure,
  MachineSignalSpn.FuelRate,
  MachineSignalSpn.BatteryVoltage,
];

/** Catalog signals indexed by SPN, used to name fault codes that match a known signal. */
export const MACHINE_SIGNALS_BY_SPN: ReadonlyMap<
  number,
  MachineSignalDefinition
> = new Map(
  MACHINE_SIGNAL_GROUPS.flatMap((group) => group.signals).map((signal) => [
    signal.spn,
    signal,
  ]),
);
